import { create } from 'zustand';

const LS_KEY = 'mdv-hole-display';
const WIDTH_LS_KEY = 'mdv-hole-width';
const DESURVEY_LS_KEY = 'mdv-hole-desurvey';
const LABELS_LS_KEY = 'mdv-hole-labels';
const LITH_HIDDEN_LS_KEY = 'mdv-hidden-lith';
const INTERVAL_VIS_LS_KEY = 'mdv-hole-interval-vis';

export const DEFAULT_HOLE_WIDTH_SCALE = 1;
export const MIN_HOLE_WIDTH_SCALE = 0.25;
export const MAX_HOLE_WIDTH_SCALE = 5;

/** Default cylinder radii in scene units (metres). Assay sits outside lith on the same path. */
export const HOLE_TRACE_RADIUS = 0.15;
export const HOLE_INTERVAL_RADIUS = 0.5;
export const HOLE_LITH_RADIUS = 0.32;
export const HOLE_HIGHLIGHT_RADIUS = 0.22;
export const HOLE_TRACE_BOX = 0.3;
export const HOLE_LEGACY_ASSAY_RADIUS = 0.2;
export const HOLE_ASSAY_SPHERE_RADIUS = 0.5;

export function clampHoleWidthScale(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_HOLE_WIDTH_SCALE;
  return Math.min(MAX_HOLE_WIDTH_SCALE, Math.max(MIN_HOLE_WIDTH_SCALE, n));
}

export function holeDisplayRadii(scale = DEFAULT_HOLE_WIDTH_SCALE) {
  const s = clampHoleWidthScale(scale);
  return {
    hole: HOLE_TRACE_RADIUS * s,
    interval: HOLE_INTERVAL_RADIUS * s,
    lith: HOLE_LITH_RADIUS * s,
    highlight: HOLE_HIGHLIGHT_RADIUS * s,
    holeBox: HOLE_TRACE_BOX * s,
    legacyAssay: HOLE_LEGACY_ASSAY_RADIUS * s,
    assaySphere: HOLE_ASSAY_SPHERE_RADIUS * s,
    lineWidth: Math.max(1, Math.round(3 * s)),
    highlightLineWidth: Math.max(2, Math.round(5 * s)),
  };
}

function loadHoleWidthScale() {
  try {
    const raw = localStorage.getItem(WIDTH_LS_KEY);
    if (raw == null || raw === '') return DEFAULT_HOLE_WIDTH_SCALE;
    return clampHoleWidthScale(JSON.parse(raw));
  } catch {
    return DEFAULT_HOLE_WIDTH_SCALE;
  }
}

function persistHoleWidthScale(scale) {
  try {
    localStorage.setItem(WIDTH_LS_KEY, JSON.stringify(clampHoleWidthScale(scale)));
  } catch {
    // quota or private mode
  }
}

function loadDesurveyEnabled() {
  try {
    const raw = localStorage.getItem(DESURVEY_LS_KEY);
    if (raw == null || raw === '') return false;
    const parsed = JSON.parse(raw);
    return parsed === true || parsed === 1 || parsed === '1';
  } catch {
    return false;
  }
}

function persistDesurveyEnabled(enabled) {
  try {
    localStorage.setItem(DESURVEY_LS_KEY, JSON.stringify(!!enabled));
  } catch {
    // quota or private mode
  }
}

function loadShowHoleLabels() {
  try {
    const raw = localStorage.getItem(LABELS_LS_KEY);
    if (raw == null || raw === '') return false;
    const parsed = JSON.parse(raw);
    return parsed === true || parsed === 1 || parsed === '1';
  } catch {
    return false;
  }
}

function persistShowHoleLabels(enabled) {
  try {
    localStorage.setItem(LABELS_LS_KEY, JSON.stringify(!!enabled));
  } catch {
    // quota or private mode
  }
}

function loadHiddenLithCodes() {
  try {
    const raw = localStorage.getItem(LITH_HIDDEN_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((code) => String(code || '').trim()).filter(Boolean);
  } catch {
    return [];
  }
}

function persistHiddenLithCodes(codes) {
  try {
    localStorage.setItem(LITH_HIDDEN_LS_KEY, JSON.stringify(codes || []));
  } catch {
    // quota or private mode
  }
}

function loadIntervalVisibleByLayer() {
  try {
    const raw = localStorage.getItem(INTERVAL_VIS_LS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const next = {};
    Object.keys(data).forEach((key) => {
      const row = data[key];
      next[key] = {
        assay: row?.assay !== false,
        lith: row?.lith === true,
      };
    });
    return next;
  } catch {
    return {};
  }
}

function persistIntervalVisibleByLayer(map) {
  try {
    localStorage.setItem(INTERVAL_VIS_LS_KEY, JSON.stringify(map || {}));
  } catch {
    // quota or private mode
  }
}

function loadIntervalModes() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
    const next = {};
    Object.keys(data).forEach((key) => {
      next[key] = data[key] === 'lith1_code' ? 'lith1_code' : 'assay';
    });
    return next;
  } catch {
    return {};
  }
}

function persistIntervalModes(intervalModeByLayer) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(intervalModeByLayer || {}));
  } catch {
    // quota or private mode
  }
}

export function isDatabaseHolesLayer(model) {
  return String(model?.type || '').toLowerCase() === 'databaseholes';
}

export function isLithInterval(mode) {
  return mode === 'lith1_code' || mode === 'lithology' || mode === 'geology';
}

export function resolvedHoleIntervalMode(model, intervalModeByLayer = {}) {
  const override = intervalModeByLayer?.[model?.name];
  if (override) return isLithInterval(override) ? 'lith1_code' : 'assay';
  return isLithInterval(model?.interval_mode) ? 'lith1_code' : 'assay';
}

export function resolvedIntervalVisible(layerName, intervalVisibleByLayer = {}) {
  const row = intervalVisibleByLayer?.[layerName];
  return {
    assay: row?.assay !== false,
    lith: row?.lith === true,
  };
}

export function isLithCodeVisible(code, hiddenLithCodes = []) {
  const key = String(code || '').trim();
  if (!key) return false;
  return !hiddenLithCodes.includes(key);
}

export const useHoleDisplayStore = create((set, get) => ({
  intervalModeByLayer: loadIntervalModes(),
  intervalVisibleByLayer: loadIntervalVisibleByLayer(),
  legendOpenByLayer: {},
  holesLegendOpen: false,
  holeWidthScale: loadHoleWidthScale(),
  desurveyEnabled: loadDesurveyEnabled(),
  showHoleLabels: loadShowHoleLabels(),
  hiddenLithCodes: loadHiddenLithCodes(),

  setHoleWidthScale: (scale) => {
    const holeWidthScale = clampHoleWidthScale(scale);
    persistHoleWidthScale(holeWidthScale);
    set({ holeWidthScale });
  },

  setDesurveyEnabled: (enabled) => {
    const desurveyEnabled = !!enabled;
    persistDesurveyEnabled(desurveyEnabled);
    set({ desurveyEnabled });
  },

  setShowHoleLabels: (enabled) => {
    const showHoleLabels = !!enabled;
    persistShowHoleLabels(showHoleLabels);
    set({ showHoleLabels });
  },

  setIntervalVisible: (layerName, kind, visible) => {
    if (!layerName || (kind !== 'assay' && kind !== 'lith')) return;
    const intervalVisibleByLayer = {
      ...get().intervalVisibleByLayer,
      [layerName]: {
        ...resolvedIntervalVisible(layerName, get().intervalVisibleByLayer),
        [kind]: !!visible,
      },
    };
    persistIntervalVisibleByLayer(intervalVisibleByLayer);
    set({ intervalVisibleByLayer });
  },

  toggleIntervalVisible: (layerName, kind) => {
    const current = resolvedIntervalVisible(layerName, get().intervalVisibleByLayer);
    const next = !current[kind];
    get().setIntervalVisible(layerName, kind, next);
    return next;
  },

  toggleLithCodeHidden: (code) => {
    const key = String(code || '').trim();
    if (!key) return;
    const current = get().hiddenLithCodes || [];
    const hiddenLithCodes = current.includes(key)
      ? current.filter((item) => item !== key)
      : [...current, key];
    persistHiddenLithCodes(hiddenLithCodes);
    set({ hiddenLithCodes });
  },

  setIntervalMode: (layerName, mode) => {
    if (!layerName) return;
    const intervalModeByLayer = {
      ...get().intervalModeByLayer,
      [layerName]: isLithInterval(mode) ? 'lith1_code' : 'assay',
    };
    persistIntervalModes(intervalModeByLayer);
    set({ intervalModeByLayer });
  },

  toggleIntervalMode: (layerName, currentMode) => {
    const next = isLithInterval(currentMode) ? 'assay' : 'lith1_code';
    get().setIntervalMode(layerName, next);
    return next;
  },

  setLegendOpen: (layerName, open) => {
    if (!layerName) return;
    const legendOpenByLayer = { ...get().legendOpenByLayer };
    if (open) legendOpenByLayer[layerName] = true;
    else delete legendOpenByLayer[layerName];
    set({ legendOpenByLayer });
  },

  toggleLegend: (layerName) => {
    if (!layerName) return;
    const open = !get().legendOpenByLayer[layerName];
    get().setLegendOpen(layerName, open);
  },

  closeLegend: (layerName) => get().setLegendOpen(layerName, false),

  setHolesLegendOpen: (open) => set({ holesLegendOpen: !!open }),
  toggleHolesLegend: () => set((state) => ({ holesLegendOpen: !state.holesLegendOpen })),
}));

export function useHoleDisplayRadii() {
  const scale = useHoleDisplayStore((s) => s.holeWidthScale);
  return holeDisplayRadii(scale);
}
